import { Inject, Injectable } from '@angular/core';

import { Environment, ENVIRONMENT } from '@sondermind/utilities/models-environment';

import {
  IFilterFormValues,
} from '@sondermind/data-access/public-facing/filter-form';
import { IDataLayer, IUserLoadedDataLayer } from '../models/gtm-data-layer.interface';
import {
  GtmCustomEvents,
  GtmExternalCalActions,
  GtmIntakeActions,
} from '../models/gtm-events.enum';

declare function trackEvent(eventType: string, details: any): void;

interface IGtmLayerWindow extends Window {
  dataLayer?: IDataLayer[];
}

declare let window: IGtmLayerWindow;

@Injectable({
  providedIn: 'root'
})
export class GtmDataLayerService {
  constructor(
    @Inject(ENVIRONMENT)
    private env: Environment
  ) {}

  set dataLayer(data: IDataLayer) {
    // eslint-disable-next-line no-extra-boolean-cast
    if (!!window.dataLayer) {
      window.dataLayer.push(data);
    }
  }

  sendPendoEvent(action: string): void {
    this.dataLayer = {
      event: 'pendo-track-event',
      action
    };
  }

  sendUserTherapyReadiness(eventType: string, label: string): void {
    this.dataLayer = {
      event: eventType,
      category: eventType,
      action: GtmIntakeActions.SUBMIT_THERAPY_READINESS,
      label
    };
  }

  /*
     * wrapper for better unit testing
     */
  getCurrentISOTime(): string {
    const currentDate = new Date();

    return currentDate.toISOString();
  }

  sendCalEvent(action: GtmExternalCalActions): void {
    this.dataLayer = {
      event: GtmCustomEvents.CAL_CONNECT_CLICKED,
      category: GtmCustomEvents.CAL_CONNECT_CLICKED,
      action,
      label: this.getCurrentISOTime()
    };
  }

  sendCalType(calType: GtmExternalCalActions): void {
    this.dataLayer = {
      event: GtmCustomEvents.CAL_EVENT_CLICKED,
      category: GtmCustomEvents.CAL_EVENT_CLICKED,
      type: calType,
      label: this.getCurrentISOTime()
    };
  }

  sendUserLoadedEvent(values: IUserLoadedDataLayer): void {
    let idWithPrefix: string = '';
    if (this.env.staging) {
      idWithPrefix = `staging-${values.user_id}`;
      // eslint-disable-next-line no-param-reassign
      values.user_id = idWithPrefix;
    }
    this.dataLayer = values;
  }

  sendProviderDirectoryInitialState(state: string, selectedIn: 'route' | 'modal'): void {
    this.dataLayer = {
      event: GtmCustomEvents.PROVIDER_DIRECTORY_INITIAL_STATE,
      category: GtmCustomEvents.PROVIDER_DIRECTORY_INITIAL_STATE,
      providerDirectoryInitialState: state,
      providerDirectoryStateSelectionMethod: selectedIn
    };
  }

  sendProviderDirectorySessionRequested(
    matchFlowUrl: string,
    providerIndex: number,
    providerUrl: string
  ): void {
    this.dataLayer = {
      event: GtmCustomEvents.PROVIDER_DIRECTORY_REQUEST_SESSION,
      category: GtmCustomEvents.PROVIDER_DIRECTORY_REQUEST_SESSION,
      providerDirectoryMatchFlowUrl: matchFlowUrl,
      providerDirectoryIndex: providerIndex,
      providerDirectoryUrl: providerUrl
    };
  }

  sendClickViewProfileEvent(profileLinkUrl: string, pageIndex: number): void {
    this.dataLayer = {
      event: GtmCustomEvents.PROVIDER_DIRECTORY_VIEW_PROFILE,
      category: GtmCustomEvents.PROVIDER_DIRECTORY_VIEW_PROFILE,
      providerDirectoryUrl: profileLinkUrl,
      providerDirectoryIndex: pageIndex
    };
  }

  sendNewDirectoryProviderSetLoadedEvent(formValues: Partial<IFilterFormValues>, providerCount: number): void {
    const selectedStateFilter = formValues?.state;
    const selectedGendersFilters = formValues?.genders?.map((x) => x.title)?.join(',');
    const selectedSpecialtiesFilters = formValues?.specialties?.map((x) => x.title)?.join(',');
    const selectedModalitiesFilters = formValues?.modalities?.map((x) => x.title)?.join(',');
    const selectedInsuranceIdFilter = formValues?.insurance?.pncId;
    const selectedInsuranceTypeFilter = formValues?.insurance?.type;

    this.dataLayer = {
      event: GtmCustomEvents.PROVIDER_DIRECTORY_LOAD_NEW_PROVIDERS,
      category: GtmCustomEvents.PROVIDER_DIRECTORY_LOAD_NEW_PROVIDERS,
      totalProviderCount: providerCount,
      selectedStateFilter,
      selectedGendersFilters,
      selectedSpecialtiesFilters,
      selectedModalitiesFilters,
      selectedInsuranceIdFilter,
      selectedInsuranceTypeFilter
    };
  }

  sendDirectoryPaginationEvent(formValues: Partial<IFilterFormValues>, isFinalResultPage: boolean): void {
    const selectedStateFilter = formValues?.state;
    const selectedGendersFilters = formValues?.genders?.map((x) => x.title)?.join(',');
    const selectedSpecialtiesFilters = formValues?.specialties?.map((x) => x.title)?.join(',');
    const selectedModalitiesFilters = formValues?.modalities?.map((x) => x.title)?.join(',');
    const selectedInsuranceIdFilter = formValues?.insurance?.pncId;
    const selectedInsuranceTypeFilter = formValues?.insurance?.type;

    this.dataLayer = {
      event: GtmCustomEvents.PROVIDER_DIRECTORY_PAGINATION,
      category: GtmCustomEvents.PROVIDER_DIRECTORY_PAGINATION,
      isFinalResultPage,
      selectedStateFilter,
      selectedGendersFilters,
      selectedSpecialtiesFilters,
      selectedModalitiesFilters,
      selectedInsuranceIdFilter,
      selectedInsuranceTypeFilter
    };
  }
}
